import React, { FC, useEffect, useState } from 'react';

import { useQuery } from '@apollo/react-hooks';
import Box from '@material-ui/core/Box';
import CssBaseline from '@material-ui/core/CssBaseline';
import md5 from 'md5';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet';
import { I18nextProvider } from 'react-i18next';
import { Route, Switch, useLocation } from 'react-router-dom';

import { ProtectedRoute } from '../../components/ProtectedRoute';
import { AUTH_APP_ID_KEY } from '../../const';
import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { GET_TENANT_INFO } from '../../graphql/tenant';
import { GET_LEARNER_INFO } from '../../graphql/user';
import i18n from '../../i18n';
import { AuthErrorPage } from '../../pages/AuthErrorPage';
import { InvitePage } from '../../pages/InvitePage';
import SubscriptionPage from '../../pages/SubscriptionPage';
import { JOIN_TEAM_ROUTE } from '../../routes';
import { LearnerInfo, Query, USER_ROLES } from '../../types';
import {
  updateUserComPlanInfo,
  handleUserComPayload,
  isSubscriptionOwnerInfo,
} from '../../utils/helpers';
import { useAuth, useSubscriptionInfo } from '../../utils/hooks';
import { Layout } from '../Layout';
import { RXDLayout } from '../RXDLayout';
import useStyles from './styles';

const getRoleForUserCom = (userInfo: LearnerInfo | null | undefined) => {
  switch (userInfo?.role) {
    case USER_ROLES.CompanyOwner:
      return 'Company Owner';
    case USER_ROLES.TeamManager:
      return 'Manager';
    case USER_ROLES.B2BLearner:
      return 'B2B learner';
    case USER_ROLES.B2CLearner:
      return 'B2C learner';
    default:
      return null;
  }
};

interface CommonLayoutProps {
  setAuthAppId: React.Dispatch<React.SetStateAction<string>>;
  hasAuthenticatedCookie: boolean;
}

const CommonLayout: FC<CommonLayoutProps> = ({ setAuthAppId, hasAuthenticatedCookie }) => {
  const classes = useStyles();
  const { isInitialized, isAuthenticated, isLoading } = useAuth();
  const isUserComLoaded = useScriptLoader(SCRIPTS.USER_COM);
  const isVerseLoaded = useScriptLoader(SCRIPTS.VERSE);

  const [isInitializedGtm, setIsInitializedGtm] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isVerseLoaded) {
      const videoEvents = window?.videoEvents?.default;
      if (videoEvents) {
        window.verseEvents = new videoEvents(null);
        if (window.verseEvents?.onHotspotClick) {
          window.verseEvents?.onHotspotClick((payload) =>
            window.open(window.location.origin + payload),
          );
        }
      }
    }
  }, [isVerseLoaded]);

  const { loadSubInfo, subInfo } = useSubscriptionInfo();

  const { data: tenantData, loading: loadingTenant } = useQuery(GET_TENANT_INFO);
  const tenant = tenantData?.getTenantInfo;
  const customCss = tenant?.custom_css;

  const { data: learnerData, loading: learnerLoading, refetch: refetchUserInfo } = useQuery<
    Pick<Query, 'getLearnerInfo'>
  >(GET_LEARNER_INFO, {
    errorPolicy: 'all',
    skip: !isInitialized || !isAuthenticated,
  });

  const userInfo = learnerData?.getLearnerInfo;

  const chargeBeeSite = userInfo?.sub_provided_by_chargebee_site || tenant?.chargebee_site;

  //fetch subscription info after authorization
  useEffect(() => {
    if (userInfo?.role && Object.values(USER_ROLES).includes(userInfo.role as USER_ROLES)) {
      loadSubInfo(userInfo.role as USER_ROLES);
    }
  }, [userInfo, loadSubInfo]);

  useEffect(() => {
    if (subInfo?.type || subInfo?.renews_automaticly_on) {
      const subOwnerInfo = isSubscriptionOwnerInfo(subInfo);
      updateUserComPlanInfo({
        plan: subInfo?.type,
        renewDate: subInfo?.renews_automaticly_on,
        trialLeft: subOwnerInfo ? subOwnerInfo?.remaining_trials?.left : 0,
        userInfo,
      });
    }
  }, [subInfo, userInfo]);

  useEffect(() => {
    const idFromStorage = localStorage.getItem(AUTH_APP_ID_KEY);
    const idFromRequest = tenant?.auth0_app_id || '';
    if (idFromStorage !== idFromRequest && idFromRequest) {
      setAuthAppId(idFromRequest);
      localStorage.setItem(AUTH_APP_ID_KEY, idFromRequest);
    }
  }, [tenant, setAuthAppId]);

  useEffect(() => {
    if (isUserComLoaded && isInitialized && !isLoading && window?.UE?.pageHit) {
      if (isAuthenticated) {
        if (userInfo) {
          const role = getRoleForUserCom(userInfo);

          window?.UE?.pageHit({
            apiKey: process.env.REACT_APP_USERCOM_API_KEY,
            user_id:
              process.env.REACT_APP_BUILD_TYPE === 'prod'
                ? md5(userInfo.id)
                : `${md5(userInfo.id)}_${process.env.REACT_APP_BUILD_TYPE}`,
            email: userInfo?.email,
            referrer_url: document?.referrer,
            has_subscription: Boolean(userInfo?.has_subscription),
            owned_subscription: Boolean(userInfo?.owned_subscription),
            ...(userInfo?.name && { name: userInfo.name }),
            ...(userInfo.teams?.length && { team_id: userInfo?.teams?.[0]?.id }),
            ...(userInfo.teams?.length && { team_name: userInfo?.teams?.[0]?.name }),
            ...(userInfo?.company?.name && { company_name: userInfo.company.name }),
            ...(role && { role }),
            onPayloadReceived: handleUserComPayload(userInfo),
          });
        }
      } else {
        window?.UE?.pageHit({
          apiKey: process.env.REACT_APP_USERCOM_API_KEY,
          referrer_url: document?.referrer,
          onPayloadReceived: handleUserComPayload(null),
        });
      }
    }
  }, [isAuthenticated, isInitialized, isLoading, userInfo, isUserComLoaded]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      tenant?.gtm_id !== undefined &&
      isInitialized &&
      !isLoading &&
      !learnerLoading
    ) {
      setIsInitializedGtm(true);
      const tagManagerArgs = {
        gtmId: tenant?.gtm_id,
        ...(userInfo?.id && { userid: userInfo?.id }),
      };

      TagManager.initialize(tagManagerArgs);
    }
  }, [tenant, isInitialized, isLoading, userInfo, learnerLoading]);

  useEffect(() => {
    // @ts-ignore
    if (isInitializedGtm && !userInfo && window?.dataLayer) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'logged-out-page-visit',
      });
    }
  }, [isInitializedGtm, location.pathname, userInfo]);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles?.parentNode?.removeChild(jssStyles);
    }
  }, []);

  const waitForUser = hasAuthenticatedCookie && (!isInitialized || isLoading || learnerLoading);

  return (
    <I18nextProvider i18n={i18n}>
      <Box className={classes.container}>
        <CssBaseline />
        <Helmet>
          <style>{customCss}</style>
        </Helmet>
        <Switch>
          <Route
            path={JOIN_TEAM_ROUTE}
            render={(props) => (
              <InvitePage
                {...props}
                tenant={tenant}
                userInfo={userInfo}
                userLoading={learnerLoading}
                refetchUserInfo={refetchUserInfo}
              />
            )}
          />
          <ProtectedRoute
            exact
            path="/pricing/:id"
            withSubscription={false}
            userInfo={userInfo}
            userLoading={learnerLoading}
            render={(props) => (
              <SubscriptionPage
                {...props}
                logoUrl={tenant?.logo_url}
                chargeBeeSite={chargeBeeSite}
                userInfo={userInfo}
              />
            )}
          />
          <Route
            path="/rxd"
            render={(props) => (
              <RXDLayout
                tenant={tenant}
                userInfo={userInfo}
                userLoading={learnerLoading}
                {...props}
              />
            )}
          />
          <Route path="/auth-error" render={(props) => <AuthErrorPage {...props} />} />
          <Route
            path="/"
            render={(props) => (
              <Layout
                tenant={tenant}
                loadingTenant={loadingTenant}
                userInfo={userInfo}
                userLoading={learnerLoading}
                waitForUser={waitForUser}
                {...props}
              />
            )}
          />
        </Switch>
      </Box>
    </I18nextProvider>
  );
};

export default CommonLayout;
